import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { ArrowLeft, Camera, X, Image as ImageIcon, Calendar } from "lucide-react";
import Cropper, { Area } from "react-easy-crop";
import getCroppedImg from "../../../../utils/cropImage";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useSelector } from "react-redux";
import styles from "./Step4MemberProfile.module.css";
import parentStyles from "../MemberOnboarding.module.css";
import MousePointerClick from "../../CreatorApplication/icons/MousePointerClick";
import AvatarPlaceholderIcon from "../icons/AvatarPlaceholderIcon";
import { showToast } from "../../../../utils/toast";

interface Step4Props {
  onNext: (data?: any) => void;
  onBack: () => void;
  savedData?: any;
  canGoBack?: boolean;
}

export const formatDOBToYYYYMMDD = (dobString: string): string => {
  if (!dobString) return "";
  const cleaned = dobString.trim();

  // Already YYYY-MM-DD or YYYY/MM/DD
  const ymdMatch = cleaned.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  // DD/MM/YYYY or MM/DD/YYYY
  const dmyMatch = cleaned.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/);
  if (dmyMatch) {
    let [, p1, p2, year] = dmyMatch;
    let day = p1;
    let month = p2;
    // If second number is > 12, user entered MM/DD/YYYY
    if (parseInt(p2, 10) > 12 && parseInt(p1, 10) <= 12) {
      month = p1;
      day = p2;
    }
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  // Fallback: Date parse
  const parsedDate = new Date(cleaned);
  if (!isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  return cleaned;
};

export const formatDOBToDDMMYYYY = (dobString: string): string => {
  if (!dobString) return "";
  const cleaned = dobString.trim();

  // If already DD/MM/YYYY or DD-MM-YYYY
  const dmyMatch = cleaned.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/);
  if (dmyMatch) {
    let [, p1, p2, year] = dmyMatch;
    let day = p1;
    let month = p2;
    if (parseInt(p2, 10) > 12 && parseInt(p1, 10) <= 12) {
      month = p1;
      day = p2;
    }
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  }

  // If YYYY-MM-DD
  const ymdMatch = cleaned.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return `${day.padStart(2, "0")}/${month.padStart(2, "0")}/${year}`;
  }

  const parsedDate = new Date(cleaned);
  if (!isNaN(parsedDate.getTime())) {
    const year = parsedDate.getFullYear();
    const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
    const day = String(parsedDate.getDate()).padStart(2, "0");
    return `${day}/${month}/${year}`;
  }

  return cleaned;
};

export const getPickerValueFromBirthDate = (str: string): string => {
  if (!str) return "";
  const cleaned = str.trim();
  const dmyMatch = cleaned.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})$/);
  if (dmyMatch) {
    let [, p1, p2, year] = dmyMatch;
    let day = p1;
    let month = p2;
    if (parseInt(p2, 10) > 12 && parseInt(p1, 10) <= 12) {
      month = p1;
      day = p2;
    }
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  const ymdMatch = cleaned.match(/^(\d{4})[-\/.](\d{1,2})[-\/.](\d{1,2})$/);
  if (ymdMatch) {
    const [, year, month, day] = ymdMatch;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return "";
};

export const calculateAge = (dobString: string): number | null => {
  if (!dobString) return null;
  const isoDate = formatDOBToYYYYMMDD(dobString);
  if (!isoDate) return null;
  const parts = isoDate.split("-");
  if (parts.length !== 3) return null;
  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

  const birthDate = new Date(year, month, day);
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

const schema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  username: yup.string().required("Username is required"),
  birthDate: yup
    .string()
    .required("Birth date is required")
    .test("is-18-years-old", "You must be at least 18 years old", function (value) {
      if (!value) return false;
      const age = calculateAge(value);
      if (age === null || isNaN(age)) {
        return this.createError({ message: "Please enter a valid birth date" });
      }
      if (age < 18) {
        return this.createError({ message: "You must be at least 18 years old" });
      }
      return true;
    }),
  aboutMe: yup.string().required("About me is required"),
  lookingFor: yup.string().required("This field is required"),
}).required();

type FormData = yup.InferType<typeof schema>;

export default function Step4MemberProfile({
  onNext,
  onBack,
  savedData,
  canGoBack = true,
}: Step4Props) {
  const reduxUser = useSelector((state: any) => state.user?.user);
  let localUser: any = null;
  try {
    const stored = localStorage.getItem("user");
    if (stored) localUser = JSON.parse(stored);
  } catch (e) { }

  const initialUsername =
    savedData?.username ||
    reduxUser?.username ||
    localUser?.username ||
    "";

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [profileImageFile, setProfileImageFile] = useState<File | null>(savedData?.profileImg || null);
  const [profileImage, setProfileImage] = useState<string | null>(savedData?.profileImagePreview || null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const datePickerRef = useRef<HTMLInputElement>(null);

  const onCropComplete = useCallback((croppedArea: Area, currentCroppedAreaPixels: Area) => {
    setCroppedAreaPixels(currentCroppedAreaPixels);
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.addEventListener("load", () => {
        setImageSrc(reader.result?.toString() || null);
      });
      reader.readAsDataURL(file);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleSaveCrop = async () => {
    try {
      if (imageSrc && croppedAreaPixels) {
        const croppedImageFile = await getCroppedImg(imageSrc, croppedAreaPixels);
        if (croppedImageFile) {
          const previewUrl = URL.createObjectURL(croppedImageFile);
          setProfileImage(previewUrl);
          setProfileImageFile(croppedImageFile);
          setIsModalOpen(false);
          setImageSrc(null);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleCancel = () => {
    setImageSrc(null);
    setIsModalOpen(false);
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      firstName: savedData?.firstName || reduxUser?.firstName || localUser?.firstName || "",
      lastName: savedData?.lastName || reduxUser?.lastName || localUser?.lastName || "",
      username: initialUsername,
      birthDate: formatDOBToDDMMYYYY(savedData?.birthDate || savedData?.DOB || ""),
      aboutMe: savedData?.aboutMe || "",
      lookingFor: savedData?.lookingFor || savedData?.whatAreYouLookingFor || "",
    },
  });

  const birthDateValue = watch("birthDate");

  const maxBirthDateForPicker = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().split("T")[0];
  }, []);

  const openDatePicker = () => {
    if (datePickerRef.current) {
      if (typeof datePickerRef.current.showPicker === "function") {
        try {
          datePickerRef.current.showPicker();
        } catch (e) {
          datePickerRef.current.focus();
        }
      } else {
        datePickerRef.current.focus();
      }
    }
  };

  const handleDatePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value; // YYYY-MM-DD
    if (!val) return;
    const parts = val.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts;
      const formatted = `${day}/${month}/${year}`;
      setValue("birthDate", formatted, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  };

  useEffect(() => {
    if (initialUsername) {
      setValue("username", initialUsername, { shouldValidate: true });
    }
  }, [initialUsername, setValue]);

  const onSubmit = (data: FormData) => {
    const formattedDOB = formatDOBToYYYYMMDD(data.birthDate);
    const payload = {
      profileImg: profileImageFile,
      profileImagePreview: profileImage,
      firstName: data.firstName.trim(),
      lastName: data.lastName.trim(),
      username: data.username.trim(),
      birthDate: data.birthDate.trim(),
      DOB: formattedDOB,
      aboutMe: data.aboutMe.trim(),
      lookingFor: data.lookingFor.trim(),
      whatAreYouLookingFor: data.lookingFor.trim(),
    };
    console.log("[Step4MemberProfile] Form submitted -> calling onNext:", payload);
    onNext(payload);
  };

  return (
    <div className={styles.formCard}>
      <h1 className={styles.formTitle}>Build your member profile</h1>
      <p className={styles.formSubtitle}>
        Let creators know a little about you. You control what's visible.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.photoUploadContainer}>
          <div className={styles.photoCircle} onClick={() => setIsModalOpen(true)} style={profileImage ? { overflow: 'hidden', padding: 0 } : {}}>
            {profileImage ? (
              <img src={profileImage} alt="Profile preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : (
              <AvatarPlaceholderIcon />
            )}
            <div className={styles.cameraBadge}>
              <Camera size={12} color="#ffffff" />
            </div>
          </div>
          <div className={styles.photoTitle}>
            Upload a profile photo<span>*</span>
          </div>
          <div className={styles.photoInstruction}>
            JPEG or PNG, min 400x400px. Used on your public profile.
          </div>
        </div>

        <div className={`${styles.formGrid} ${styles.nameGrid}`}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>First name <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Enter first name"
              {...register("firstName")}
            />
            {errors.firstName && <p className={parentStyles.errorText}>{errors.firstName.message}</p>}
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Last name <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Enter last name"
              {...register("lastName")}
            />
            {errors.lastName && <p className={parentStyles.errorText}>{errors.lastName.message}</p>}
          </div>
        </div>

        <div className={`${styles.formGrid} ${styles.usernameBirthGrid}`}>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Username <span>*</span></label>
            <input
              type="text"
              className={styles.input}
              placeholder="Choose a unique username"
              {...register("username")}
            />
            {errors.username && <p className={parentStyles.errorText}>{errors.username.message}</p>}
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Birth date <span>*</span></label>
            <div className={styles.inputWrapper}>
              <div
                className={styles.calendarIconClickable}
                onClick={openDatePicker}
                title="Open calendar"
              >
                <Calendar size={18} />
              </div>
              <input
                type="text"
                placeholder="dd/mm/yyyy"
                className={styles.inputWithIcon}
                {...register("birthDate")}
                onClick={openDatePicker}
              />
              <input
                ref={datePickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                value={getPickerValueFromBirthDate(birthDateValue || "")}
                max={maxBirthDateForPicker}
                onChange={handleDatePickerChange}
                className={styles.hiddenDatePicker}
              />
            </div>
            {errors.birthDate && <p className={parentStyles.errorText}>{errors.birthDate.message}</p>}
          </div>
        </div>

        <div className={styles.inputGroupFull} style={{ marginTop: '36px' }}>
          <label className={styles.label}>About Me <span>*</span></label>
          <textarea
            className={styles.textarea}
            placeholder="Tell in short"
            {...register("aboutMe")}
          />
          {errors.aboutMe && <p className={parentStyles.errorText}>{errors.aboutMe.message}</p>}
        </div>

        <div className={styles.inputGroupFull}>
          <label className={styles.label}>What you're looking for <span>*</span></label>
          <textarea
            className={styles.textarea}
            placeholder="Tell in short"
            {...register("lookingFor")}
          />
          {errors.lookingFor && <p className={parentStyles.errorText}>{errors.lookingFor.message}</p>}
        </div>

        <div className={styles.actionRow}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={() => {
              if (!canGoBack) {
                showToast("Cannot go backward", "info");
                return;
              }
              onBack();
            }}
            title={!canGoBack ? "Cannot go backward" : undefined}
            style={!canGoBack ? { opacity: 0.8, cursor: "not-allowed" } : undefined}
          >
            <ArrowLeft size={18} /> Back
          </button>
          <button
            type="submit"
            className={styles.continueBtn}
          >
            Continue
          </button>
        </div>
      </form>

      {isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <button className={styles.closeBtn} onClick={() => setIsModalOpen(false)}>
              <X size={20} />
            </button>
            <h2 className={styles.modalTitle}>Upload profile picture</h2>

            {imageSrc ? (
              <>
                <div className={styles.cropperContainer}>
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={1}
                    cropShape="round"
                    showGrid={false}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                  />
                </div>
                <div className={styles.controlsContainer}>
                  <ImageIcon className={styles.zoomIcon} />
                  <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    aria-labelledby="Zoom"
                    onChange={(e) => setZoom(Number(e.target.value))}
                    className={styles.slider}
                  />
                  <ImageIcon className={styles.zoomIcon} />
                </div>
                <div className={styles.modalActionRow}>
                  <button type="button" className={styles.modalCancelBtn} onClick={handleCancel}>Cancel</button>
                  <button type="button" className={styles.modalUploadBtn} onClick={handleSaveCrop}>Upload</button>
                </div>
              </>
            ) : (
              <>
                <div className={styles.uploadArea} onClick={handleUploadClick}>
                  <div className={styles.uploadIconContainer}>
                    <MousePointerClick />
                  </div>
                  <div className={styles.uploadText}>
                    Drag and drop JPEG,<br />PNG, SVG or GIF (max.<br />800x400 px)
                  </div>
                </div>

                <div className={styles.orDivider}>or</div>

                <button type="button" className={styles.uploadOutlineBtn} onClick={handleUploadClick}>
                  Upload A Photo
                </button>

                <div className={styles.modalActionRow}>
                  <button type="button" className={styles.modalCancelBtn} onClick={handleCancel}>
                    Cancel
                  </button>
                  <button type="button" className={styles.modalUploadBtn} disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                    Upload
                  </button>
                </div>
              </>
            )}

            <input
              type="file"
              accept="image/jpeg, image/png, image/svg+xml, image/gif"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
